import { useMutation } from "@tanstack/react-query";

/** Return type of useImperativeFetch. */
export interface ImperativeFetchState<TData> {
  /** The most recently fetched data, or null. */
  data: TData | null;
  /** Whether a fetch is in progress. */
  isFetching: boolean;
  /** The most recent error, or null. */
  error: Error | null;
}

/** Options for {@link useImperativeFetch}. */
interface UseImperativeFetchOptions {
  /**
   * Error message surfaced via the global Toast when the fetch rejects.
   * Mirrors the ``meta.errorMessage`` convention used by ``useMutation``
   * call sites — the app's ``MutationCache.onError`` renders it via
   * ``parseApiError`` so users get feedback when an Apply/refetch fails.
   */
  errorMessage?: string;
}

/**
 * Imperative data fetching hook — no caching, no query keys.
 *
 * Returns a `fetch` function that the caller invokes explicitly (e.g. from
 * a button handler). Each call always hits the network.
 *
 * Implemented as a thin wrapper over `useMutation` so rejections flow
 * through the app-level ``MutationCache.onError`` → ``parseApiError`` →
 * Toast pipeline — the same sink used by mutation hooks. Previously this
 * hook exposed errors only via local state and every call site wrapped
 * its ``fetch(...)`` in ``.catch(() => {})``, silently swallowing
 * failures (no toast, stale data left on screen). Routing through
 * `useMutation` means callers no longer need to report errors themselves.
 *
 * @param fetchFn - Async function that performs the actual API call.
 *   Receives the params passed to `fetch()` and returns the data.
 * @param options - Configuration. ``errorMessage`` is surfaced to the user
 *   as the Toast title when the fetch rejects.
 *
 * @example
 * ```ts
 * const { data, isFetching, fetch } = useImperativeFetch(
 *   async (params: { id: string }) => {
 *     const res = await api.get(`/items/${params.id}`);
 *     return res.data;
 *   },
 *   { errorMessage: "Failed to load item" }
 * );
 *
 * // In a handler:
 * await fetch({ id: "123" });
 * ```
 */
export function useImperativeFetch<TData, TParams = void>(
  fetchFn: (params: TParams) => Promise<TData>,
  options: UseImperativeFetchOptions = {}
): ImperativeFetchState<TData> & {
  fetch: (params: TParams) => Promise<TData>;
  /**
   * Resets the hook to its initial state — clears ``data`` / ``error`` and
   * sets ``isFetching`` back to false. Use to discard a previous result
   * (e.g. clearing a grid once the record it was loaded from is deleted).
   */
  reset: () => void;
} {
  const mutation = useMutation({
    mutationFn: fetchFn,
    meta: options.errorMessage
      ? { errorMessage: options.errorMessage }
      : undefined
  });

  return {
    data: mutation.data ?? null,
    isFetching: mutation.isPending,
    error: mutation.error,
    fetch: mutation.mutateAsync,
    reset: mutation.reset
  };
}
